import React, { Component } from 'react'
import {
    Button, Descriptions, Drawer, Input, message, Modal, Select, Space, Spin, Table, Tabs, Tag, Typography
} from 'antd'
import { ApiOutlined, CloudServerOutlined, DatabaseOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons'
import {
    getVibeAdminAppDetail,
    getVibeAdminAppHealth,
    getVibeAdminProjectApis,
    saveVibeAdminProjectApis,
    syncVibeAdminDatabaseReadme,
    updateVibeAdminDatabaseReadme,
} from '../../request'

const { Paragraph, Text } = Typography
const { TabPane } = Tabs

const STATUS_MAP = {
    approved: { text: '待部署', color: 'processing' },
    provisioning: { text: '接入中', color: 'processing' },
    ready: { text: '已就绪', color: 'success' },
    failed: { text: '失败', color: 'error' },
    disabled: { text: '禁用', color: 'default' },
    deleted: { text: '已删除', color: 'default' },
}

const DEPLOYMENT_MODE_META = {
    server: { text: '服务器部署', color: 'blue' },
    local: { text: '仅本地运行', color: 'default' },
}

const BUSINESS_SUBJECT_META = {
    lechun: { text: '乐纯', color: 'green' },
    superbody: { text: '超体', color: 'orange' },
}

const LEGACY_DEPLOY_PROMPT_INTRO = '请在你的业务项目目录里，把下面说明粘贴给 Codex 执行初始化。'
const DEPLOY_PROMPT_INTRO = `你现在要帮我初始化一个公司 VibeCoding 服务器部署项目。

这个项目需要部署到公司服务器上，通过 GitLab CI 构建 Docker 镜像，并发布到指定访问地址。`

// 2026-07-20 coder(lq): Share one application detail implementation across all Vibe admin entrances.
export default class VibeAppDetailDrawer extends Component {
    state = {
        loading: false,
        detail: null,
        healthLoading: false,
        health: null,
        syncing: false,
        editVisible: false,
        editContent: '',
        editSaving: false,
        apiLoading: false,
        apiSaving: false,
        allApis: [],
        selectedApiCodes: [],
        apiKeyword: '',
        apiSourceSystem: '',
    }

    componentDidUpdate(prevProps) {
        const opened = this.props.visible && !prevProps.visible
        const projectChanged = this.props.visible && String(this.props.projectId) !== String(prevProps.projectId)
        if ((opened || projectChanged) && this.props.projectId) {
            this.loadDetail()
            this.loadProjectApis()
        }
    }

    loadProjectApis = () => {
        const { projectId } = this.props
        if (!projectId) return
        this.setState({
            apiLoading: true,
            allApis: [],
            selectedApiCodes: [],
            apiKeyword: '',
            apiSourceSystem: '',
        })
        getVibeAdminProjectApis({ projectId })
            .then(res => this.setState({
                allApis: Array.isArray(res?.allApis) ? res.allApis : [],
                selectedApiCodes: (Array.isArray(res?.apis) ? res.apis : []).map(item => item.apiCode),
            }))
            .catch(() => {})
            .finally(() => this.setState({ apiLoading: false }))
    }

    saveProjectApis = () => {
        const { projectId } = this.props
        if (!projectId) return
        this.setState({ apiSaving: true })
        saveVibeAdminProjectApis({ projectId, apiCodes: this.state.selectedApiCodes })
            .then(res => {
                const savedApis = Array.isArray(res) ? res : []
                message.success('接口权限配置已保存')
                this.loadProjectApis()
                this.notifyUpdated({ projectId, apis: savedApis })
            })
            .catch(() => {})
            .finally(() => this.setState({ apiSaving: false }))
    }

    loadDetail = () => {
        const { projectId } = this.props
        if (!projectId) return
        this.setState({ loading: true, detail: null, health: null })
        getVibeAdminAppDetail({ id: projectId })
            .then(res => this.setState({ detail: res }))
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    getDeploymentMode = project => project?.deploymentMode === 'local' ? 'local' : 'server'

    renderStatus = (status, project) => {
        if (status === 'approved' && this.getDeploymentMode(project) === 'local') {
            return <Tag color="processing">待初始化</Tag>
        }
        const meta = STATUS_MAP[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderDeploymentMode = project => {
        const meta = DEPLOYMENT_MODE_META[this.getDeploymentMode(project)]
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderBusinessSubject = project => {
        const subject = project?.businessSubject === 'superbody' ? 'superbody' : 'lechun'
        const meta = BUSINESS_SUBJECT_META[subject]
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    formatDictionaryError = error => {
        if (!error) return ''
        return String(error).includes('404 Not Found') ? '字典文件未提交' : error
    }

    getDeploymentPrompt = detail => {
        if (!detail) return ''
        const prompt = this.getDeploymentMode(detail) === 'local'
            ? detail.localInitInstructionMd
            : detail.initInstructionMd
        return (prompt || '')
            .replace(/\\r\\n/g, '\n')
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\n')
            .replace(/\\"/g, '"')
            .replace(LEGACY_DEPLOY_PROMPT_INTRO, DEPLOY_PROMPT_INTRO)
    }

    copyDeploymentPrompt = () => {
        const prompt = this.getDeploymentPrompt(this.state.detail)
        if (!prompt) {
            message.warning('当前应用暂无部署提示词')
            return
        }
        if (!navigator.clipboard) {
            message.warning('当前浏览器不支持自动复制，请手动选择复制')
            return
        }
        navigator.clipboard.writeText(prompt).then(() => message.success('部署提示词已复制'))
    }

    notifyUpdated = () => {
        if (this.props.onUpdated) this.props.onUpdated()
    }

    getApiRequestUrl = api => {
        if (!api) return ''
        if (api.fullPath) return api.fullPath
        if (api.baseUrl && api.path) {
            return `${String(api.baseUrl).replace(/\/$/, '')}/${String(api.path).replace(/^\//, '')}`
        }
        return api.path || api.baseUrl || ''
    }

    checkHealth = () => {
        const { detail } = this.state
        if (!detail?.id) return
        this.setState({ healthLoading: true })
        getVibeAdminAppHealth({ id: detail.id })
            .then(res => this.setState({ health: res }))
            .catch(() => {})
            .finally(() => this.setState({ healthLoading: false }))
    }

    syncDatabaseReadme = () => {
        const { detail } = this.state
        if (!detail?.id) return
        this.setState({ syncing: true })
        syncVibeAdminDatabaseReadme({ id: detail.id })
            .then(res => {
                message.success('数据库字典已同步')
                this.setState({ detail: res })
                this.notifyUpdated()
            })
            .catch(() => {})
            .finally(() => this.setState({ syncing: false }))
    }

    openDictionaryEditor = () => {
        const { detail } = this.state
        if (!detail?.id) return
        this.setState({ editVisible: true, editContent: detail.databaseReadmeMd || '' })
    }

    saveDatabaseReadme = () => {
        const { detail, editContent } = this.state
        if (!detail?.id) return
        this.setState({ editSaving: true })
        updateVibeAdminDatabaseReadme({
            id: detail.id,
            databaseReadmeMd: editContent,
        }).then(res => {
            message.success('数据库字典已保存')
            this.setState({ detail: res, editVisible: false })
            this.notifyUpdated()
        }).catch(() => {})
            .finally(() => this.setState({ editSaving: false }))
    }

    render() {
        const { visible, onClose } = this.props
        const {
            loading, detail, healthLoading, health, syncing, editVisible, editContent, editSaving,
            apiLoading, apiSaving, allApis, selectedApiCodes, apiKeyword, apiSourceSystem,
        } = this.state
        const normalizedApiKeyword = apiKeyword.trim().toLowerCase()
        const apiSourceSystems = Array.from(new Set(
            allApis.map(api => api.sourceSystem).filter(Boolean)
        )).sort((left, right) => left.localeCompare(right, 'zh-CN'))
        const visibleApis = allApis.filter(api => {
            const matchesSourceSystem = !apiSourceSystem || api.sourceSystem === apiSourceSystem
            const matchesKeyword = !normalizedApiKeyword || [
                api.apiName, api.apiCode, api.sourceSystem, api.method, api.path, api.fullPath, api.baseUrl,
                this.getApiRequestUrl(api), api.description,
            ].some(value => String(value || '').toLowerCase().includes(normalizedApiKeyword))
            return matchesSourceSystem && matchesKeyword
        })
        const serverDeployment = this.getDeploymentMode(detail) === 'server'
        return (
            <Drawer
                title={detail ? `应用详情：${detail.projectName || detail.projectSlug}` : '应用详情'}
                visible={visible}
                width={900}
                onClose={onClose}
                destroyOnClose
            >
                <Space style={{ marginBottom: 16 }} wrap>
                    {serverDeployment ? (
                        <Button icon={<ReloadOutlined />} loading={healthLoading} onClick={this.checkHealth}>
                            检查运行状态
                        </Button>
                    ) : null}
                    <Button type="primary" ghost icon={<FileTextOutlined />} loading={syncing} onClick={this.syncDatabaseReadme}>
                        同步数据字典
                    </Button>
                </Space>
                <Spin spinning={loading}>
                    {detail ? (
                        <Tabs defaultActiveKey={this.props.initialTabKey || 'info'}>
                            <TabPane tab={<span><CloudServerOutlined />应用信息</span>} key="info">
                                <Descriptions bordered column={2} size="small">
                                    <Descriptions.Item label="应用名称">{detail.projectName || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="状态">{this.renderStatus(detail.status, detail)}</Descriptions.Item>
                                    <Descriptions.Item label="项目类型">{this.renderDeploymentMode(detail)}</Descriptions.Item>
                                    <Descriptions.Item label="主体">{this.renderBusinessSubject(detail)}</Descriptions.Item>
                                    <Descriptions.Item label="所属用户">{detail.ownerUserNick || detail.ownerUserName || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="GitLab 项目">
                                        {detail.gitlabWebUrl ? (
                                            <a href={detail.gitlabWebUrl} target="_blank" rel="noreferrer">
                                                {detail.gitlabProjectPath || detail.gitlabWebUrl}
                                            </a>
                                        ) : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="备注" span={2}>{detail.description || '-'}</Descriptions.Item>
                                    {serverDeployment ? (
                                        <Descriptions.Item label="公开地址" span={2}>
                                            <Paragraph copyable={detail.publicUrl ? { text: detail.publicUrl } : false} style={{ marginBottom: 0 }}>
                                                {detail.publicUrl || '-'}
                                            </Paragraph>
                                        </Descriptions.Item>
                                    ) : null}
                                    {serverDeployment ? <Descriptions.Item label="部署服务器">{detail.deployHostName || '-'}</Descriptions.Item> : null}
                                    {serverDeployment ? <Descriptions.Item label="部署端口">{detail.deployPort || '-'}</Descriptions.Item> : null}
                                    {serverDeployment ? <Descriptions.Item label="部署目录" span={2}>{detail.deployPath || '-'}</Descriptions.Item> : null}
                                    {serverDeployment ? <Descriptions.Item label="健康检查">{detail.healthPath || '-'}</Descriptions.Item> : null}
                                    <Descriptions.Item label="最近接入">{detail.lastProvisionedAt || '-'}</Descriptions.Item>
                                </Descriptions>
                                {health ? (
                                    <div style={{ marginTop: 16, padding: 12, border: '1px solid #edf0f5', background: '#fafafa' }}>
                                        <Space style={{ marginBottom: 8 }}>
                                            <Text strong>运行状态</Text>
                                            <Tag color={health.healthy ? 'success' : 'error'}>{health.healthy ? '健康' : '异常'}</Tag>
                                            {health.statusCode ? <Tag>{health.statusCode}</Tag> : null}
                                        </Space>
                                        <Paragraph copyable={health.healthUrl ? { text: health.healthUrl } : false}>
                                            {health.healthUrl || health.message || '-'}
                                        </Paragraph>
                                        {health.body ? <pre style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{health.body}</pre> : null}
                                    </div>
                                ) : null}
                            </TabPane>
                            <TabPane tab={<span><DatabaseOutlined />数据库字典</span>} key="dictionary">
                                <Space style={{ marginBottom: 12 }} wrap>
                                    <Button type="primary" ghost icon={<FileTextOutlined />} onClick={this.openDictionaryEditor}>
                                        编辑字典
                                    </Button>
                                </Space>
                                <Descriptions bordered column={2} size="small" style={{ marginBottom: 12 }}>
                                    <Descriptions.Item label="数据库">{detail.mysqlDatabaseName || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="数据库连接">{detail.mysqlConnectionName || '默认连接'}</Descriptions.Item>
                                    <Descriptions.Item label="Host/Port">{detail.mysqlHost || '-'}:{detail.mysqlPort || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="JDBC URL" span={2}>
                                        <Paragraph copyable={detail.mysqlJdbcUrl ? { text: detail.mysqlJdbcUrl } : false} style={{ marginBottom: 0 }}>
                                            {detail.mysqlJdbcUrl || '-'}
                                        </Paragraph>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="同步时间">{detail.databaseReadmeSyncedAt || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="同步错误" span={2}>
                                        {detail.databaseReadmeMd ? '-' : (this.formatDictionaryError(detail.databaseReadmeError) || '-')}
                                    </Descriptions.Item>
                                </Descriptions>
                                <pre style={{ minHeight: 260, whiteSpace: 'pre-wrap', background: '#0f172a', color: '#e5e7eb', padding: 16, overflow: 'auto' }}>
                                    {detail.databaseReadmeMd || '暂无 DATA_DICTIONARY.md 内容'}
                                </pre>
                            </TabPane>
                            <TabPane tab={<span><ApiOutlined />接口权限</span>} key="apis">
                                <Space style={{ marginBottom: 12 }} wrap>
                                    <Select
                                        allowClear
                                        value={apiSourceSystem || undefined}
                                        placeholder="全部系统"
                                        style={{ width: 180 }}
                                        onChange={value => this.setState({ apiSourceSystem: value || '' })}
                                    >
                                        {apiSourceSystems.map(sourceSystem => (
                                            <Select.Option key={sourceSystem} value={sourceSystem}>
                                                {sourceSystem}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <Input.Search
                                        allowClear
                                        value={apiKeyword}
                                        placeholder="搜索接口名称、系统、地址或说明"
                                        style={{ width: 360 }}
                                        onChange={event => this.setState({ apiKeyword: event.target.value })}
                                    />
                                    <Button type="primary" loading={apiSaving} onClick={this.saveProjectApis}>
                                        保存接口权限
                                    </Button>
                                    <Text type="secondary">已选择 {selectedApiCodes.length} 个接口</Text>
                                </Space>
                                <Spin spinning={apiLoading}>
                                    <Table
                                        rowKey="apiCode"
                                        size="small"
                                        dataSource={visibleApis}
                                        pagination={{ pageSize: 20, showSizeChanger: false }}
                                        rowSelection={{
                                            selectedRowKeys: selectedApiCodes,
                                            preserveSelectedRowKeys: true,
                                            onChange: keys => this.setState({ selectedApiCodes: keys }),
                                        }}
                                        columns={[
                                            {
                                                title: '接口',
                                                key: 'api',
                                                width: 260,
                                                render: (_, api) => (
                                                    <div>
                                                        <div>{api.apiName || api.apiCode}</div>
                                                        <Text type="secondary" code style={{ fontSize: 12 }}>{api.apiCode}</Text>
                                                    </div>
                                                ),
                                            },
                                            { title: '系统', dataIndex: 'sourceSystem', width: 130, render: value => value || '-' },
                                            {
                                                title: '接口地址',
                                                key: 'request',
                                                width: 360,
                                                render: (_, api) => (
                                                    <Space align="start">
                                                        <Tag color="blue">{api.method || '-'}</Tag>
                                                        <Paragraph
                                                            copyable={this.getApiRequestUrl(api) ? { text: this.getApiRequestUrl(api) } : false}
                                                            style={{ maxWidth: 285, marginBottom: 0 }}
                                                            ellipsis={{ rows: 2, tooltip: this.getApiRequestUrl(api) || '-' }}
                                                        >
                                                            {this.getApiRequestUrl(api) || '-'}
                                                        </Paragraph>
                                                    </Space>
                                                ),
                                            },
                                            { title: '说明', dataIndex: 'description', ellipsis: true, render: value => value || '-' },
                                        ]}
                                    />
                                </Spin>
                            </TabPane>
                            <TabPane tab={<span><FileTextOutlined />部署提示词</span>} key="deploymentPrompt">
                                <Space style={{ marginBottom: 12 }}>
                                    <Button
                                        type="primary"
                                        ghost
                                        icon={<FileTextOutlined />}
                                        disabled={!this.getDeploymentPrompt(detail)}
                                        onClick={this.copyDeploymentPrompt}
                                    >
                                        复制部署提示词
                                    </Button>
                                </Space>
                                <Input.TextArea
                                    value={this.getDeploymentPrompt(detail)}
                                    rows={24}
                                    readOnly
                                    placeholder="当前应用暂无部署提示词"
                                    style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 13, lineHeight: 1.6 }}
                                />
                            </TabPane>
                            <TabPane tab="日志" key="logs">
                                <Table
                                    size="small"
                                    rowKey="id"
                                    dataSource={detail.provisionLogs || []}
                                    pagination={false}
                                    columns={[
                                        { title: '时间', dataIndex: 'createTime', width: 170 },
                                        { title: '步骤', dataIndex: 'stepName', width: 160 },
                                        { title: '状态', dataIndex: 'stepStatus', width: 100, render: value => <Tag>{value || '-'}</Tag> },
                                        { title: '消息', dataIndex: 'message' },
                                    ]}
                                />
                            </TabPane>
                        </Tabs>
                    ) : null}
                </Spin>
                <Modal
                    title={`编辑数据库字典：${detail?.projectName || detail?.projectSlug || ''}`}
                    visible={editVisible}
                    width={900}
                    okText="保存"
                    confirmLoading={editSaving}
                    onOk={this.saveDatabaseReadme}
                    onCancel={() => this.setState({ editVisible: false })}
                    destroyOnClose
                >
                    <Input.TextArea
                        value={editContent}
                        rows={20}
                        placeholder="可粘贴或维护 DATA_DICTIONARY.md 内容"
                        onChange={event => this.setState({ editContent: event.target.value })}
                    />
                </Modal>
            </Drawer>
        )
    }
}
